// The tab bar: built to match the iOS 26 glass tab bar as closely as the web allows, because the
// native one replaces it when Musette becomes an app. A floating capsule of clear glass; the selected
// tab sits in a lens that slides between tabs on a spring. Press and the lens lifts (grows, brightens);
// drag along the bar and it follows the finger, ticking at each tab; let go and that tab opens.
// The selected tab takes the app's tint, as a native tab bar does; the rest at label strength.
import { useRef, useState } from "preact/hooks";
import { useLocation } from "preact-iso";
import { haptic } from "../lib/haptic";

const TABS: [string, string, (p: string) => boolean, preact.JSX.Element][] = [
  ["Today", "/", (p) => p === "/", <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" />],
  ["Week", "/week", (p) => p.startsWith("/week") || p.startsWith("/review"), <path d="M4 6.5h16v13H4zM4 11h16M8 4v4M16 4v4" />],
  ["Rides", "/rides", (p) => p.startsWith("/rides") || p.startsWith("/routes") || p.startsWith("/paris"), <><circle cx="5.6" cy="16" r="3.9" /><circle cx="18.4" cy="16" r="3.9" /><path d="M5.6 16h4.9l-2-6.6h6.9M10.5 16l5.3-5.4M15.4 9.4l.4 1.2L18.4 16M8.5 9.4 5.6 16M8.3 8.6l-.2-.8M6.9 7.8h2.6M15.4 9.4l1.3-.5c1.3-.1 1.6.9 1.3 1.8" /></>],
  ["Coach", "/coach", (p) => p.startsWith("/coach") || p.startsWith("/weight"), <path d="M4 5h16v11H9l-5 4z" />],
  ["Race", "/racing", (p) => p.startsWith("/racing") || p.startsWith("/tour") || p.startsWith("/mallorca"), <><path d="M8.2 3.8 4.6 5.6 2.8 9.4l2.6 1.4 1.6-1.4V20h10V9.4l1.6 1.4 2.6-1.4-1.8-3.8-3.6-1.8c-.6 1.3-2 2.1-3.8 2.1s-3.2-.8-3.8-2.1z" /><g fill="currentColor" stroke="none"><circle cx="9.6" cy="11" r="1" /><circle cx="14.4" cy="11" r="1" /><circle cx="12" cy="14.2" r="1" /><circle cx="9.6" cy="17.4" r="1" /><circle cx="14.4" cy="17.4" r="1" /></g></>],
];

export function TabBar() {
  const { path, route } = useLocation();
  const nav = useRef<HTMLElement>(null);
  // The section the screen belongs to; You, sign-in and privacy belong to none, so no lens shows.
  const active = TABS.findIndex(([, , on]) => on(path));
  const [press, setPress] = useState<{ x: number | null; i: number; from: number } | null>(null);
  const moved = useRef(false);

  const geo = () => { const r = nav.current!.getBoundingClientRect(); const pad = 4, w = (r.width - pad * 2) / TABS.length; return { r, pad, w }; };
  const indexAt = (cx: number) => { const { r, pad, w } = geo(); return Math.min(TABS.length - 1, Math.max(0, Math.floor((cx - r.left - pad) / w))); };
  const down = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const i = indexAt(e.clientX); moved.current = false;
    setPress({ x: null, i, from: i });
  };
  const move = (e: PointerEvent) => {
    if (!press) return;
    const { r, pad, w } = geo();
    const x = Math.min(r.width - pad - w, Math.max(pad, e.clientX - r.left - w / 2));
    const i = indexAt(e.clientX);
    if (!moved.current && Math.abs(x - (pad + press.from * w)) < 6) return;
    if (!moved.current) { moved.current = true; nav.current?.setPointerCapture(e.pointerId); }
    if (i !== press.i) haptic("tick");
    setPress({ ...press, x, i });
  };
  const up = () => {
    if (!press) return;
    if (moved.current) { const href = TABS[press.i][1]; if (!TABS[press.i][2](path)) { haptic("tick"); route(href); } }
    setPress(null);
  };
  const lensI = press ? press.i : active;
  const style = press?.x != null ? { transform: `translateX(${press.x - 4}px) scale(1.12)` } : { "--i": lensI } as any;
  return (
    <nav ref={nav} class={`tabbar${press ? " pressed" : ""}${press?.x != null ? " dragging" : ""}`} aria-label="Main"
      onPointerDown={down as any} onPointerMove={move as any} onPointerUp={up} onPointerCancel={() => setPress(null)}>
      {lensI >= 0 && <span class="tab-lens" aria-hidden="true" style={style} />}
      {TABS.map(([name, href, on, icon], i) => (
        <a href={href} draggable={false} aria-current={on(path) ? "page" : undefined} class={i === lensI ? "lit" : undefined}
          onClick={(e) => { if (moved.current) e.preventDefault(); else if (!on(path)) haptic("tick"); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">{icon}</svg>
          <span>{name}</span>
        </a>
      ))}
    </nav>
  );
}
